import { useCallback, useMemo } from "react";

import { RegistryLinkStatus } from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, ANY_REGISTRY_CODE, ANY_REGISTRY_ID } from "@/app/types/okta.types";

import { RegistryOption } from "../registry-dropdown.types";
import { useRegistryDropdown } from "../use-registry-dropdown";
import { RegistryDropdownWithAnyProps } from "./registry-dropdown-with-any.types";

export const useRegistryDropdownWithAny = ({
  activeRegistryCode,
  excludeAnyRegistry,
  disabledRegistryCodes,
  onRegistryChange,
}: RegistryDropdownWithAnyProps) => {
  const { loading, registries } = useRegistryDropdown();

  const registryOptions = useMemo(() => {
    if (excludeAnyRegistry) {
      return registries;
    }

    return [
      {
        registryId: ANY_REGISTRY_ID,
        registryCode: ANY_REGISTRY_CODE,
        shortName: "Any Registry",
        status: RegistryLinkStatus.Registered,
      },
      ...registries,
    ];
  }, [registries, excludeAnyRegistry]);

  const disabledRegistryOptions = useMemo(() => {
    if (!disabledRegistryCodes || !registryOptions) return [];

    return registryOptions.filter((registry) => disabledRegistryCodes.includes(registry.registryCode));
  }, [disabledRegistryCodes, registryOptions]);

  const onActiveRegistryChange = useCallback(
    (registry: RegistryOption | null) => {
      onRegistryChange((registry?.registryCode as AggregatedRegistryCode) ?? ANY_REGISTRY_CODE);
    },
    [onRegistryChange],
  );

  const activeRegistry = useMemo(() => {
    return registryOptions.find((registry) => registry.registryCode === activeRegistryCode) ?? null;
  }, [registryOptions, activeRegistryCode]);

  return {
    loading,
    registryOptions,
    disabledRegistryOptions,
    activeRegistry,
    onActiveRegistryChange,
  };
};
